import { registerMockResolver } from "@redotech/rpc/client";
import { initMockConsoleApi } from "./console-api";
import { generateMockFromSchema } from "./generate-mock-from-schema";
import { registerRpcSchema } from "./mock-registry";
import {
  getActiveFixtureId,
  getMockParam,
  getMockResponse,
  loadMockFixture,
  registerKnownRpcs,
  shouldMockRpc,
  trackRpcCall,
} from "./mock-storage";

/**
 * Initializes the mock system for development. Call this once at app startup
 * inside a `process.env.NODE_ENV === "development"` guard.
 *
 * This wires up the mock resolver (which depends on @faker-js/faker) so that
 * the core rpc client doesn't need to import faker at the module level.
 *
 * If `?mock=<id>` is in the URL (where id is not "true"), the fixture at
 * `/__mock-data/<id>.json` is loaded and applied before the app renders.
 * On subsequent reloads (where the query param may be stripped), the fixture
 * ID is read from localStorage and re-fetched to pick up file changes.
 */
export async function initMockSystem(): Promise<void> {
  const mockParam = getMockParam();
  const fixtureId =
    mockParam && mockParam !== "true" ? mockParam : getActiveFixtureId();
  if (fixtureId) {
    await loadMockFixture(fixtureId);
  }

  registerMockResolver(
    async (name, outputSchema, inlineMock) => {
      const custom = getMockResponse(name);
      if (custom !== undefined) {
        return custom;
      }

      const generated = generateMockFromSchema(outputSchema, name);
      if (inlineMock === undefined) {
        return generated;
      }
      if (typeof inlineMock === "function") {
        return inlineMock(generated);
      }
      return inlineMock;
    },
    registerRpcSchema,
    { shouldMockRpc, trackRpcCall, registerKnownRpcs },
  );

  initMockConsoleApi();
}
